import { Button as BaseButton } from '@base-ui/react/button'

import { useHydrated } from '@/hooks/use-hydrated'

/**
 * An UNSTYLED `<button>` that is safe under SSR. Use it anywhere a raw `<button>` would otherwise go —
 * bespoke controls whose look has nothing to do with `Button`'s variants (segmented toggles, list rows,
 * chips, icon affordances).
 *
 * Why this exists rather than a raw `<button>`: under full SSR the element is in the server HTML before
 * React binds its handler, so a click landing in that window is silently dropped — no handler, no error,
 * no failed assertion. That is Playwright's documented SSR flake class ("the button is enabled, but the
 * listeners have not yet been added … the click won't have any effect") and their recommended fix is
 * exactly this: keep interactive controls disabled until hydration. `useHydrated()` is false on the
 * server AND on the first client render, so the markup matches and there is no hydration mismatch; the
 * native `disabled` attribute can then only clear on THIS component's own client render — the very commit
 * that attaches its handler. Measured on a deep, late-streaming trigger: it opens 60–86ms AFTER the
 * element goes live, i.e. never early. Playwright's `enabled` actionability check gets this for free.
 *
 * Deliberately the NATIVE `disabled` attribute: base-ui's `focusableWhenDisabled` renders `aria-disabled`
 * + `tabindex="0"` INSTEAD of `disabled`, which Playwright's wait does not key on — it would look like a
 * gate and silently be none.
 *
 * APPEARANCE: being inert for hydration is an implementation detail users must never see, so while that is
 * the ONLY reason the button is disabled it renders exactly as its enabled self — no greying, no
 * `not-allowed` cursor, no flash on first paint. That is done statically, in CSS: this component sets
 * `data-hydrating`, and app.css redefines Tailwind's `disabled:` variant to `&:disabled:not([data-hydrating])`
 * so EVERY `disabled:*` utility in the codebase skips that window on its own. A button the caller actually
 * disabled has no `data-hydrating` and keeps all of its disabled styling.
 */
export function ButtonPrimitive({ disabled, ...props }: BaseButton.Props) {
  const hydrated = useHydrated()
  return (
    <BaseButton
      disabled={disabled || !hydrated}
      // Marks "inert ONLY because we haven't hydrated yet" — the caller still considers this usable. The
      // `disabled:` variant is redefined in app.css to skip elements carrying this, so no `disabled:`
      // utility (and no UA greying) applies during the hydration window. Nothing to do per call site.
      data-hydrating={!hydrated && !disabled ? '' : undefined}
      {...props}
    />
  )
}
